import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  CreateDesktopAgentPairingCodeResponse,
  DesktopAgentDevice,
  GetDesktopAgentDevicesResponse,
} from '@dreamai/types';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Modal } from 'react-bootstrap';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp.svg';
import api from '../../api';
import CircleMinusIcon from './CircleMinusIcon';
import codeDialogImage from '../../assets/whatsapp-mac-code-dialog.png';
import './whatsapp-mac-connected-app.scss';

const POLL_MS = 3000;

export function useWhatsappMacDevices(enabled: boolean): {
  devices: DesktopAgentDevice[];
  refreshDevices: () => Promise<DesktopAgentDevice[]>;
} {
  const { getAccessTokenSilently } = useAuth0();
  const [devices, setDevices] = useState<DesktopAgentDevice[]>([]);

  const refreshDevices = useCallback(async () => {
    if (!enabled) return [];
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.get<GetDesktopAgentDevicesResponse>(
        '/desktop-agent/devices',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDevices(data.devices);
      return data.devices;
    } catch (error) {
      console.error('Error loading WhatsApp Mac devices:', error);
      return [];
    }
  }, [enabled, getAccessTokenSilently]);

  useEffect(() => {
    if (!enabled) {
      setDevices([]);
      return;
    }
    void refreshDevices();
  }, [enabled, refreshDevices]);

  return { devices, refreshDevices };
}

interface WhatsappMacSectionProps {
  assistantName: string;
  devices: DesktopAgentDevice[];
  refreshDevices: () => Promise<DesktopAgentDevice[]>;
  onConnected: () => void;
}

const relativeTime = (timestamp: string) =>
  formatDistanceToNow(parseISO(timestamp), { addSuffix: true });

export const WhatsappMacSection: React.FC<WhatsappMacSectionProps> = ({
  assistantName,
  devices,
  refreshDevices,
  onConnected,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [pairingCode, setPairingCode] =
    useState<CreateDesktopAgentPairingCodeResponse | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isGettingCode, setIsGettingCode] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<DesktopAgentDevice | null>(
    null,
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(false);
  const setupOpenRef = useRef(false);
  const pollingRef = useRef(false);
  const downloadUrl = process.env.REACT_APP_WHATSAPP_MAC_DOWNLOAD_URL;
  const isExpired = pairingCode !== null && remainingSeconds <= 0;

  useEffect(() => {
    void refreshDevices();
  }, [refreshDevices]);

  useEffect(() => {
    if (!showSetupModal || !pairingCode) return undefined;
    const updateRemaining = () => {
      setRemainingSeconds(
        Math.max(
          0,
          Math.ceil((Date.parse(pairingCode.expiresAt) - Date.now()) / 1000),
        ),
      );
    };
    updateRemaining();
    if (Date.parse(pairingCode.expiresAt) <= Date.now()) return undefined;
    const timer = window.setInterval(() => {
      updateRemaining();
      if (Date.parse(pairingCode.expiresAt) <= Date.now()) {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [pairingCode, showSetupModal]);

  useEffect(() => {
    if (!showSetupModal || !pairingCode || isExpired) return undefined;
    let active = true;
    const pollDevices = async () => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      try {
        const refreshedDevices = await refreshDevices();
        if (!active || refreshedDevices.length === 0) return;
        active = false;
        setupOpenRef.current = false;
        setShowSetupModal(false);
        setPairingCode(null);
        setRemainingSeconds(0);
        setCodeError(false);
        onConnected();
      } finally {
        pollingRef.current = false;
      }
    };
    const timer = window.setInterval(() => {
      void pollDevices();
    }, POLL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [isExpired, onConnected, pairingCode, refreshDevices, showSetupModal]);

  const openSetupModal = () => {
    setupOpenRef.current = true;
    setPairingCode(null);
    setRemainingSeconds(0);
    setCodeError(false);
    setShowSetupModal(true);
  };

  const closeSetupModal = () => {
    setupOpenRef.current = false;
    setShowSetupModal(false);
    setPairingCode(null);
    setRemainingSeconds(0);
    setCodeError(false);
  };

  const handleGetCode = async () => {
    setIsGettingCode(true);
    setCodeError(false);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.post<CreateDesktopAgentPairingCodeResponse>(
        '/desktop-agent/pairing-code',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!setupOpenRef.current) return;
      setPairingCode(data);
      setRemainingSeconds(
        Math.max(
          0,
          Math.ceil((Date.parse(data.expiresAt) - Date.now()) / 1000),
        ),
      );
    } catch {
      if (setupOpenRef.current) setCodeError(true);
    } finally {
      setIsGettingCode(false);
    }
  };

  const handleRemoveDevice = async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    setRemoveError(false);
    try {
      const token = await getAccessTokenSilently();
      await api.delete(
        `/desktop-agent/devices/${encodeURIComponent(removeTarget.id)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRemoveTarget(null);
      await refreshDevices();
    } catch {
      setRemoveError(true);
    } finally {
      setIsRemoving(false);
    }
  };

  const formatCountdown = () => {
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <>
      <div className='ca-detail-block whatsapp-mac-section'>
        <h3 className='ca-block-title'>Check my WhatsApp</h3>
        <p className='ca-block-description'>
          {assistantName} reads WhatsApp on your Mac to spot messages you still
          need to reply to, and writes reply drafts for you. Nothing is ever
          sent.
        </p>
        <p className='ca-block-description'>
          Needs WhatsApp Desktop on a Mac. Drafts appear in WhatsApp on your
          Mac, not on your phone.
        </p>
        {devices.length === 0 ? (
          <button
            type='button'
            className='ca-connect-btn'
            onClick={openSetupModal}
          >
            <WhatsappIcon className='whatsapp-icon' aria-hidden='true' />
            <span>Connect my WhatsApp</span>
          </button>
        ) : (
          <div className='ca-account-list'>
            {devices.map((device) => (
              <div className='ca-account-row' key={device.id}>
                <div className='ca-account-info'>
                  <span className='ca-account-icon'>
                    <WhatsappIcon
                      className='whatsapp-icon'
                      aria-hidden='true'
                    />
                  </span>
                  <span className='ca-account-label'>{device.deviceName}</span>
                  <span className='ca-account-scope'>
                    {device.lastSeenAt
                      ? `Last seen ${relativeTime(device.lastSeenAt)}`
                      : 'Not seen yet'}
                  </span>
                </div>
                <button
                  type='button'
                  className='ca-remove-btn'
                  onClick={() => {
                    setRemoveError(false);
                    setRemoveTarget(device);
                  }}
                  aria-label={`Disconnect ${device.deviceName}`}
                >
                  <CircleMinusIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        show={showSetupModal}
        onHide={closeSetupModal}
        centered
        dialogClassName='whatsapp-mac-setup-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Connect WhatsApp on your Mac</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ol className='whatsapp-mac-steps'>
            <li>
              <p>Download Dream for Mac.</p>
              {downloadUrl ? (
                <a
                  className='ca-connect-btn'
                  href={downloadUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Download for Mac
                </a>
              ) : (
                <button type='button' className='ca-connect-btn' disabled>
                  Download for Mac
                </button>
              )}
            </li>
            <li>
              Open Dream and allow Accessibility access when your Mac asks.
            </li>
            <li>
              <p>Dream will ask for a code. Click Show code and type it in.</p>
              <img
                src={codeDialogImage}
                alt='The Dream app on your Mac asking for a code'
                className='whatsapp-mac-code-image'
              />
              {!pairingCode && (
                <button
                  type='button'
                  className='ca-connect-btn'
                  disabled={isGettingCode}
                  onClick={handleGetCode}
                >
                  {isGettingCode ? 'Getting code…' : 'Show code'}
                </button>
              )}
              {pairingCode && !isExpired && (
                <div className='whatsapp-mac-code-details'>
                  <div className='whatsapp-mac-pairing-code'>
                    {pairingCode.code}
                  </div>
                  <p className='whatsapp-mac-countdown'>
                    Expires in {formatCountdown()}
                  </p>
                  <p className='whatsapp-mac-countdown'>
                    Type this into the Dream app on your Mac, not into WhatsApp.
                  </p>
                </div>
              )}
              {isExpired && (
                <>
                  <p className='whatsapp-mac-error'>This code has expired.</p>
                  <button
                    type='button'
                    className='ca-connect-btn'
                    disabled={isGettingCode}
                    onClick={handleGetCode}
                  >
                    {isGettingCode ? 'Getting code…' : 'Get new code'}
                  </button>
                </>
              )}
              {codeError && (
                <p className='whatsapp-mac-error'>
                  Couldn&apos;t get a code. Try again.
                </p>
              )}
              <p className='whatsapp-mac-hint'>
                Closed it? Click Dream in your Mac&apos;s menu bar, then Enter
                code…
              </p>
            </li>
          </ol>
        </Modal.Body>
      </Modal>

      <Modal
        show={removeTarget !== null}
        onHide={() => !isRemoving && setRemoveTarget(null)}
        centered
        dialogClassName='whatsapp-mac-disconnect-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Disconnect this Mac?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assistantName} will stop checking WhatsApp on{' '}
          {removeTarget?.deviceName}.
          {removeError && (
            <p className='whatsapp-mac-error'>
              Couldn&apos;t remove this Mac. Try again.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='ca-back-btn'
            disabled={isRemoving}
            onClick={() => setRemoveTarget(null)}
          >
            Cancel
          </button>
          <button
            type='button'
            className='ca-connect-btn'
            disabled={isRemoving}
            onClick={handleRemoveDevice}
          >
            Disconnect
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
